import React, { useEffect, useState, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { gql, useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Link from '@material-ui/core/Link'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import useAchievementsAuth from 'lib/achievements-auth/useAchievementsAuth'
import { decodeToken } from 'lib/achievements-auth/token'
import useStyles from './styles'
import pgaLogo from 'images/pga-logo-navy.png'

const VERIFY_GOLFER_OTP = gql`
  mutation VerifyGolferOtp($input: VerifyGolferOtpInput!) {
    verifyGolferOtp(input: $input) {
      success
      message
      token
      claimToken
      holeInOne {
        id
      }
    }
  }
`

const AchievementsVerifyPage = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { login } = useAchievementsAuth()
  const isMounted = useRef(true)

  const [verificationState, setVerificationState] = useState('loading')
  const [errorMessage, setErrorMessage] = useState('')

  const searchParams = new URLSearchParams(location.search)
  const token = searchParams.get('token')
  const claimToken = searchParams.get('claimToken')

  const [verifyOtp] = useMutation(VERIFY_GOLFER_OTP, {
    onCompleted: data => {
      if (!isMounted.current) return
      const {
        success,
        message,
        token: jwtToken,
        claimToken: returnedClaimToken,
        holeInOne,
      } = data.verifyGolferOtp

      if (!success || !jwtToken) {
        setVerificationState('error')
        setErrorMessage(message || 'Verification failed. Please try again.')
        return
      }

      const decoded = decodeToken(jwtToken)
      const user = {
        email: decoded?.golfer_email,
        name: decoded?.golfer_name,
      }

      login(jwtToken, user)
      setVerificationState('success')

      setTimeout(() => {
        if (isMounted.current) {
          // If this was a claim flow verification and we have a hole in one ID, redirect to detail page
          const effectiveClaimToken = returnedClaimToken || claimToken
          if (effectiveClaimToken && holeInOne?.id) {
            history.push(`/achievements/${holeInOne.id}`)
          } else if (effectiveClaimToken) {
            // Fallback to claim page if hole in one ID not available
            history.push(
              `/achievements/claim?token=${effectiveClaimToken}&verified=true`,
            )
          } else {
            // Standard login flow - redirect to achievements list
            history.push('/achievements')
          }
        }
      }, 1500)
    },
    onError: err => {
      if (!isMounted.current) return
      setVerificationState('error')
      setErrorMessage(
        err.message || 'The verification link is invalid or has expired.',
      )
    },
  })

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!token) {
      setVerificationState('error')
      setErrorMessage('No verification token provided.')
      return
    }

    verifyOtp({
      variables: {
        input: {
          token,
        },
      },
      context: { skipAuth: true },
    })
  }, [token, verifyOtp])

  const renderContent = () => {
    if (verificationState === 'loading') {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography className={classes.loadingText}>
            Verifying your identity...
          </Typography>
        </Box>
      )
    }

    if (verificationState === 'success') {
      return (
        <Box className={classes.successContainer}>
          <CheckCircleOutlineIcon className={classes.successIcon} />
          <Typography className={classes.successTitle}>
            Verification Successful
          </Typography>
          <Typography className={classes.successDescription}>
            {claimToken
              ? 'You have been successfully verified. Redirecting to your achievement...'
              : 'You have been successfully authenticated. Redirecting to your achievements...'}
          </Typography>
        </Box>
      )
    }

    return (
      <Box className={classes.errorContainer}>
        <ErrorOutlineIcon className={classes.errorIcon} />
        <Typography className={classes.errorTitle}>
          Verification Failed
        </Typography>
        <Typography className={classes.errorDescription}>
          {errorMessage}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          className={classes.actionButton}
          onClick={() => history.push('/achievements/login')}
        >
          BACK TO LOGIN
        </Button>
        <Typography className={classes.helpLink}>
          Need help?{' '}
          <Link href="mailto:support@pga.com" className={classes.supportLink}>
            Contact Support
          </Link>
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.logoContainer}>
        <img src={pgaLogo} alt="PGA of America" className={classes.logo} />
        <Box className={classes.taglineDivider} />
        <Typography className={classes.tagline}>We love this Game</Typography>
      </Box>

      <Box className={classes.card}>{renderContent()}</Box>

      <Typography className={classes.footer}>
        © {new Date().getFullYear()} PGA of America. All rights reserved.
      </Typography>
    </Box>
  )
}

export default AchievementsVerifyPage
